import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Box } from "@chakra-ui/react";
import environment, { BarkioOnly, BibinoOnly, getAppValue } from "src/utils/environment";
import { hasDarkVersion, hasBrowserDarkVersion } from "src/utils/getImageSrc";
import { H2 } from "src/atoms/typography";
import SvgContainer from "src/components/SvgContainer";
import CorrespondingAvatar from "src/modules/avatar/components/CorrespondingAvatar";
import MotionIndicator from "src/modules/station/components/MotionIndicator";
import BabyLottieAnimation from "src/modules/avatar/components/BabyLottieAnimation";
import DogAvatar from "src/modules/avatar/components/DogAvatar";
import { DeviceName } from "src/modules/device/components/DeviceSpec";
import { Activities } from "src/modules/activity-log/activity";
import AnimationBox from "../../components/AnimationBox";
import { ReactComponent as MotionDetachIcon } from "./detach-motion.svg";
import { AvatarModes } from "src/modules/avatar/enum";
import { AvatarProfile } from "../../components/AvatarContainers";

const { isBrowser } = environment;
const darkVersionChecker = isBrowser ? hasBrowserDarkVersion : hasDarkVersion;

export default function Motion({
  showSmallAvatar,
  selectedAvatar,
  isInDetachMode,
  isInTileView,
  isMultiRoom,
  avatarSize,
  jid
}) {
  const [dogAvatarTheme, setDogAvatarTheme] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    async function getDogAvatarTheme() {
      const theme = (await darkVersionChecker(`noisy-${selectedAvatar?.avatarCustomization?.breedAppearance}`, {
        directory: "dogs"
      }))
        ? "dark"
        : "light";
      setDogAvatarTheme(theme);
    }
    getDogAvatarTheme();
  }, [selectedAvatar?.avatarCustomization?.breedAppearance]);

  const displayProfileImage =
    selectedAvatar && selectedAvatar.previewMode === AvatarModes.profileImage && selectedAvatar.profileImageFile;
  return (
    <Flex
      width="100%"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      {...(!isInDetachMode && {
        flex: "1 1 auto"
      })}
    >
      <Box
        sx={{
          "@media (max-height: 140px)": {
            display: "none"
          }
        }}
      >
        {showSmallAvatar && <CorrespondingAvatar width={avatarSize} height={avatarSize} avatar={selectedAvatar} />}

        {!showSmallAvatar && displayProfileImage && <AvatarProfile avatar={selectedAvatar} />}

        {!showSmallAvatar && !displayProfileImage && (
          <AnimationBox>
            <BibinoOnly
              browserProps={{ useTheme: false }}
              sex={selectedAvatar?.sex}
              {...selectedAvatar?.avatarCustomization}
              skinColor={selectedAvatar?.avatarCustomization?.skinColor || selectedAvatar?.moreDataJson?.skinColor}
              animationName="motion.json"
            >
              {BabyLottieAnimation}
            </BibinoOnly>
            {dogAvatarTheme && (
              <BarkioOnly
                imageName={`${dogAvatarTheme}-noisy-${
                  selectedAvatar?.avatarCustomization?.breedAppearance || "default"
                }.svg`}
              >
                {DogAvatar}
              </BarkioOnly>
            )}
          </AnimationBox>
        )}
      </Box>
      {isMultiRoom && <DeviceName deviceJid={jid} activity={Activities.MOTION} />}
      <Box
        display="none"
        sx={{
          "@media (max-height: 140px)": {
            display: "inline-block"
          }
        }}
      >
        <SvgContainer mb="8px" icon={MotionDetachIcon} size="16px" fill="white" />
      </Box>
      <H2
        // fontSize={isInDetachMode ? "6.9vmin" : ["20px", "28px", "32px"]}
        fontSize={isInDetachMode ? "6.9vmin" : "clamp(2rem, 3.82vmin, 3.2rem)"}
        // lineHeight={isInDetachMode ? "8.7vmin" : ["24px", "32px", "40px"]}
        letterSpacing={["0.24px", "0.34px", "0.38px"]}
        color={getAppValue({
          bibino: "white",
          barkio: "highEmphasis"
        })}
        mt={isInDetachMode ? "8px" : "16px"}
        mb={isInDetachMode ? "2.5vmin" : "16px"}
        {...(isInDetachMode && {
          sx: {
            "@media (max-height: 140px)": {
              fontSize: "10px !important",
              lineHeight: "1.1 !important",
              mt: "0px !important"
            }
          }
        })}
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        maxWidth={isInDetachMode ? "90vw" : isInTileView ? "calc(100% + 102px)" : "60vw"}
      >
        {selectedAvatar
          ? t(selectedAvatar.sex === "male" ? "maleIsMoving" : "femaleIsMoving", { value0: selectedAvatar.name })
          : t("avatarIsMoving")}
      </H2>
      <Flex maxHeight="20px">
        <MotionIndicator mode="audio" />
      </Flex>
    </Flex>
  );
}
